// Second half of the mock backend — real-contract routes for profiles, exposure,
// subscribers, contacts (notes/labels), sharing, and public profile. Called by
// MockBackend.handle. Response envelopes match the live server (see mock.ts header).
import type { MockBackend } from './mock.js';
import { mkErr } from './mock.js';
import type { RequestOpts } from './http.js';
import type { Profile, ShareLink, Contact, Note } from './types.js';

const now = () => new Date().toISOString();
let seq = 500;
const id = (p: string) => `${p}_${++seq}`;

export async function handle2<T>(
  self: MockBackend, p: string, method: string, body: any,
  _opts: RequestOpts, m: (re: RegExp) => RegExpMatchArray | null, r: (v: unknown) => T,
): Promise<T> {
  const db = self.db;

  // ---- Profiles ----
  const withCount = (prof: Profile) => ({
    ...prof,
    exposed_field_count: Object.values(db.exposures[prof.id] || {}).filter(Boolean).length,
  });
  if (p === '/profiles' && method === 'GET') return r({ profiles: db.profiles.map(withCount) });
  if (p === '/profiles' && method === 'POST') {
    const prof: Profile = { id: id('prf'), name: body.name || 'New profile', description: body.description, icon: body.icon, color: body.color, is_public: !!body.is_public, avatar_url: null, created_at: now(), updated_at: now() };
    db.profiles.push(prof); db.exposures[prof.id] = {}; return r(prof);
  }
  if (m(/^\/profiles\/([^/]+)$/) && method === 'GET') {
    const prof = db.profiles.find((x) => x.id === m(/^\/profiles\/([^/]+)$/)![1]);
    if (!prof) throw mkErr(404, 'not_found', 'Profile not found.'); return r(withCount(prof));
  }
  if (m(/^\/profiles\/([^/]+)$/) && method === 'PATCH') {
    const prof = db.profiles.find((x) => x.id === m(/^\/profiles\/([^/]+)$/)![1]);
    if (!prof) throw mkErr(404, 'not_found', 'Profile not found.');
    for (const k of ['name', 'description', 'icon', 'color', 'is_public'] as const) if (body[k] !== undefined) (prof as any)[k] = body[k];
    prof.updated_at = now(); return r(prof);
  }
  if (m(/^\/profiles\/([^/]+)$/) && method === 'DELETE') {
    const pid = m(/^\/profiles\/([^/]+)$/)![1]; db.profiles = db.profiles.filter((x) => x.id !== pid); return r(undefined);
  }
  const avatarMatch = m(/^\/profiles\/([^/]+)\/avatar$/);
  if (avatarMatch && method === 'PUT') {
    const prof = db.profiles.find((x) => x.id === avatarMatch[1]); if (prof) prof.avatar_url = body.url || `mock://avatar/${avatarMatch[1]}`;
    return r({ status: 'avatar_set' });
  }
  if (avatarMatch && method === 'DELETE') {
    const prof = db.profiles.find((x) => x.id === avatarMatch[1]); if (prof) prof.avatar_url = null; return r(undefined);
  }
  // Real server: PUT /profiles/{id}/exposure with {entries:[{field_id,exposed}]}. GET reads it back.
  const expMatch = m(/^\/profiles\/([^/]+)\/exposure$/);
  if (expMatch && method === 'PUT') {
    const pid = expMatch[1]; db.exposures[pid] = {};
    for (const e of body.entries || []) db.exposures[pid][e.field_id] = !!e.exposed;
    return r({ status: 'exposure_updated' });
  }
  if (expMatch && method === 'GET') {
    const pid = expMatch[1]; const map = db.exposures[pid] || {};
    const exposures = Object.keys(map).filter((fid) => map[fid]).map((fid) => ({ field_id: fid, exposed: true }));
    return r({ exposures });
  }

  // ---- Subscribers (owner side) — GET list + DELETE revoke ----
  if (p === '/subscribers' && method === 'GET') return r({ subscribers: db.subs });
  const subDel = m(/^\/subscribers\/([^/]+)$/);
  if (subDel && method === 'DELETE') {
    db.subs = db.subs.filter((x) => x.id !== subDel[1]);
    return r(undefined);
  }

  // ---- My contacts (subscriber side) ----
  if (p === '/contacts' && method === 'GET') return r({ contacts: db.contacts });
  const cMatch = m(/^\/contacts\/([^/]+)$/);
  if (cMatch && method === 'GET') {
    const c = db.contacts.find((x) => x.id === cMatch[1]);
    if (!c) throw mkErr(404, 'not_found', 'Contact not found.'); return r(c);
  }
  if (cMatch && method === 'DELETE') { db.contacts = db.contacts.filter((x) => x.id !== cMatch[1]); return r(undefined); }

  // Accept a pending reciprocal share-back contact (My Contacts "Add" button).
  const acceptMatch = m(/^\/contacts\/([^/]+)\/accept$/);
  if (acceptMatch && method === 'POST') {
    const key = acceptMatch[1];
    const c = db.contacts.find((x) => x.id === key || x.subscription_id === key);
    if (!c) throw mkErr(404, 'not_found', 'Contact not found.');
    c.pending_approval = false;
    c.status = 'active';
    (c as any).live = true;
    return r({ status: 'active', subscription_id: c.subscription_id || c.id || key });
  }

  // Notes: POST /contacts/{id}/notes, PATCH/DELETE /contacts/{id}/notes/{noteID}
  const noteCreate = m(/^\/contacts\/([^/]+)\/notes$/);
  if (noteCreate && method === 'POST') {
    const c = db.contacts.find((x) => x.id === noteCreate[1]); if (!c) throw mkErr(404, 'not_found', 'Contact not found.');
    const note: Note = { id: id('note'), body: body.body, created_at: now(), updated_at: now() };
    (c.notes ||= []).push(note); return r(note);
  }
  const noteMut = m(/^\/contacts\/([^/]+)\/notes\/([^/]+)$/);
  if (noteMut && method === 'PATCH') {
    const c = db.contacts.find((x) => x.id === noteMut[1]); const note = c?.notes?.find((n) => n.id === noteMut[2]);
    if (!note) throw mkErr(404, 'not_found', 'Note not found.');
    note.body = body.body; note.updated_at = now(); return r(note);
  }
  if (noteMut && method === 'DELETE') {
    const c = db.contacts.find((x) => x.id === noteMut[1]); if (c?.notes) c.notes = c.notes.filter((n) => n.id !== noteMut[2]);
    return r(undefined);
  }
  // Labels: PUT /contacts/{id}/labels
  const labelMatch = m(/^\/contacts\/([^/]+)\/labels$/);
  if (labelMatch && method === 'PUT') {
    const c = db.contacts.find((x) => x.id === labelMatch[1]); if (c) c.labels = body.labels || [];
    return r({ labels: body.labels || [] });
  }

  // ---- Sharing ----
  if (p === '/share-links' && method === 'GET') return r({ share_links: db.links });
  if (p === '/share-links' && method === 'POST') {
    const expires_at = body.expires_in_sec ? new Date(Date.now() + body.expires_in_sec * 1000).toISOString() : null;
    const link: ShareLink = { id: id('lnk'), profile_id: body.profile_id, token: id('tok'), label: body.label, max_uses: body.max_uses ?? null, use_count: 0, expires_at, revoked_at: null, created_at: now() };
    db.links.unshift(link); return r(link);
  }
  const linkDel = m(/^\/share-links\/([^/]+)$/);
  if (linkDel && method === 'DELETE') {
    const l = db.links.find((x) => x.id === linkDel[1]); if (l) l.revoked_at = now();
    return r(undefined);
  }
  // Redeem a share → POST /subscribe {token} → returns a contactResp.
  if (p === '/subscribe' && method === 'POST') {
    const c: Contact = { id: id('sub'), subscription_id: '', owner_id: 'idn_alex', profile_id: 'prf_ext',
      display_name: 'New Contact', status: 'active', sync_state: 'live', labels: [], notes: [], added_at: now(), last_synced_at: now(), fields: [] };
    db.contacts.push(c); return r(c);
  }

  // ---- Public profile ----
  const pubMatch = m(/^\/public\/([^/]+)$/);
  if (pubMatch && method === 'GET') {
    const pid = db.publicProfile.profile_id || '';
    const map = db.exposures[pid] || {};
    const prof = db.profiles.find((x) => x.id === pid);
    const fields = db.fields.filter((f) => map[f.id]).map((f) => ({ type: f.type || '', label: f.label || f.type || '', value: f.value || '', key: f.type }));
    return r({ handle: pubMatch[1], display_name: 'Alex Kim', profile: prof ? { name: prof.name, description: prof.description, icon: prof.icon, color: prof.color, avatar_url: prof.avatar_url } : undefined, fields });
  }

  throw mkErr(404, 'not_found', `Mock: unhandled ${method} ${p}`);
}
